import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { customerAPI } from '../../services/api';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorAlert from '../../components/ErrorAlert';

// Customer-side personal settings page. Shows read-only identity (name,
// email, role, last login), a change-password form, and per-user overrides
// for the customer notification preferences. Overrides take precedence over
// the company-level preferences for this user's own email delivery only.

const NOTIFICATION_KEYS = [
  ['request_created', 'Request Created'],
  ['request_status', 'Request Status Updates'],
  ['invoice_created', 'New Invoice'],
  ['invoice_status', 'Invoice Status Updates'],
  ['manifest_updates', 'Manifest / Inbound Updates'],
  ['crossdock_updates', 'Cross-Dock Updates'],
  ['receiving_updates', 'Receiving Updates'],
  ['shipment_updates', 'Shipment Updates']
];

const formatDateTime = (value) => {
  if (!value) return 'Never';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 'Never' : d.toLocaleString();
};

const formatRoleLabel = (role) => {
  if (!role) return 'User';
  return String(role)
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const CustomerUserSettings = () => {
  const queryClient = useQueryClient();

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Company profile gives us the company-level notification preferences so
  // we can show an effective checkbox state when the user has no explicit
  // override.
  const { data: companyProfile } = useQuery(
    ['customer-company-profile'],
    async () => {
      const response = await customerAPI.getProfile();
      return response.data || response;
    },
    { staleTime: 60000 }
  );

  const {
    data: settings,
    isLoading,
    isError,
    error
  } = useQuery(['customer-my-settings'], async () => {
    const response = await customerAPI.getMySettings();
    return response.data || response;
  });

  const companyPrefs = companyProfile?.notificationPreferences || {};
  const userOverrides = settings?.notificationPreferences || {};

  // Compute the effective state for each notification key: the user's
  // explicit override if present, otherwise the company value, otherwise
  // default (true).
  const effectivePrefs = useMemo(() => {
    const out = {};
    for (const [key] of NOTIFICATION_KEYS) {
      if (Object.prototype.hasOwnProperty.call(userOverrides, key)) {
        out[key] = userOverrides[key] !== false;
      } else if (Object.prototype.hasOwnProperty.call(companyPrefs, key)) {
        out[key] = companyPrefs[key] !== false;
      } else {
        out[key] = true;
      }
    }
    return out;
  }, [userOverrides, companyPrefs]);

  const [savingPrefs, setSavingPrefs] = useState(false);

  const handleTogglePref = async (key, checked) => {
    setSavingPrefs(true);
    try {
      await customerAPI.updateMyNotificationPreferences({ [key]: checked });
      await queryClient.invalidateQueries(['customer-my-settings']);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update preference');
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleResetPref = async (key) => {
    setSavingPrefs(true);
    try {
      // Passing null clears the user override so the company default applies.
      await customerAPI.updateMyNotificationPreferences({ [key]: null });
      await queryClient.invalidateQueries(['customer-my-settings']);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset preference');
    } finally {
      setSavingPrefs(false);
    }
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    setPasswordError('');

    if (passwordData.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      await customerAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      toast.success('Password changed successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <ErrorAlert message={error?.message || 'Error loading settings'} />;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-brand-navy">My Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Personal account information, password, and notification preferences.
        </p>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-brand-navy mb-4">Profile</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-gray-500">Name</div>
            <div className="text-gray-900">{settings?.name || 'N/A'}</div>
          </div>
          <div>
            <div className="text-gray-500">Email</div>
            <div className="text-gray-900">{settings?.email || 'N/A'}</div>
          </div>
          <div>
            <div className="text-gray-500">Role</div>
            <div className="text-gray-900">{formatRoleLabel(settings?.role)}</div>
          </div>
          <div>
            <div className="text-gray-500">Last Login</div>
            <div className="text-gray-900">{formatDateTime(settings?.lastLogin)}</div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-brand-navy mb-1">Email Preferences</h2>
        <p className="text-sm text-gray-500 mb-4">
          These overrides apply to emails sent to your personal address. When
          left at the default, your company's preferences apply.
        </p>
        <div className="space-y-3">
          {NOTIFICATION_KEYS.map(([key, label]) => {
            const hasOverride = Object.prototype.hasOwnProperty.call(userOverrides, key);
            return (
              <div key={key} className="flex items-center justify-between text-sm">
                <label className="flex items-center text-gray-700">
                  <input
                    type="checkbox"
                    disabled={savingPrefs}
                    checked={effectivePrefs[key] === true}
                    onChange={(e) => handleTogglePref(key, e.target.checked)}
                    className="mr-3 rounded border-gray-300 text-brand-gold focus:ring-brand-gold"
                  />
                  {label}
                  {hasOverride && (
                    <span className="ml-2 inline-flex items-center rounded bg-brand-gold/10 px-2 py-0.5 text-xs font-medium text-brand-gold">
                      Override
                    </span>
                  )}
                </label>
                {hasOverride && (
                  <button
                    type="button"
                    onClick={() => handleResetPref(key)}
                    className="text-xs text-gray-500 hover:text-brand-navy underline"
                    disabled={savingPrefs}
                  >
                    Reset to company default
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-brand-navy mb-1">Change Password</h2>
        <p className="text-sm text-gray-500 mb-4">
          Minimum 8 characters, with at least one uppercase, one lowercase,
          and one number.
        </p>
        <form onSubmit={handlePasswordChange} className="max-w-md space-y-4">
          {passwordError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {passwordError}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <input
              type="password"
              required
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData((prev) => ({ ...prev, currentPassword: e.target.value }))}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input
              type="password"
              required
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData((prev) => ({ ...prev, newPassword: e.target.value }))}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <input
              type="password"
              required
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData((prev) => ({ ...prev, confirmPassword: e.target.value }))}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm"
            />
          </div>
          <div>
            <button
              type="submit"
              disabled={changingPassword}
              className="rounded-md border border-brand-navy bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:bg-brand-navy/90 disabled:opacity-50"
            >
              {changingPassword ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerUserSettings;
